import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRight,
  MessageSquare,
  TrendingUp,
  Users,
  Sparkles,
  CheckCircle2,
  Zap,
  Rocket,
  Globe,
  Check,
  Crown,
  LogOut
} from 'lucide-react';

export default function HomePage() {
  const { user, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-primary" />
                <span className="text-xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                  ProductWorkshop
                </span>
              </div>
              <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Blog
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <span className="text-sm text-muted-foreground hidden sm:inline">{user.email}</span>
                  <Link to="/dashboard">
                    <Button size="sm" variant="outline">
                      Dashboard
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={signOut}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Link to="/login">
                  <Button size="sm">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background gradient blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl opacity-20"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary">
                <Sparkles className="h-4 w-4" />
                <span>Trusted by product teams worldwide</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight">
                <span className="block text-foreground">Transform feedback into</span>
                <span className="block bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  product excellence
                </span>
              </h1>

              <p className="text-xl text-muted-foreground leading-relaxed">
                The complete platform for collecting, prioritizing, and acting on customer feedback.
                Build products your users actually want.
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4 pt-4">
                {user ? (
                  <Link to="/dashboard">
                    <Button size="lg" className="min-w-[200px]">
                      Go to Dashboard
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login">
                    <Button size="lg" className="min-w-[200px]">
                      Start Free Trial
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
              </div>

              <div className="flex flex-wrap gap-6 pt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span>30-day money back</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border">
                <img
                  src="/women on laptop.jpg"
                  alt="ProductWorkshop Dashboard - Woman using the platform"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Everything you need to listen to your customers
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Purpose-built for product teams who care about building the right things
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<MessageSquare className="h-6 w-6" />}
              title="Collect Feedback"
              description="Beautiful voting portals that your customers will actually use. Fully branded to match your product."
              color="from-blue-500 to-cyan-500"
            />
            <FeatureCard
              icon={<TrendingUp className="h-6 w-6" />}
              title="Prioritize with Data"
              description="Let your users vote on what matters most. See what's trending and make informed product decisions."
              color="from-purple-500 to-pink-500"
            />
            <FeatureCard
              icon={<Users className="h-6 w-6" />}
              title="Engage Your Community"
              description="Comments, updates, and status changes keep everyone in the loop. Build trust through transparency."
              color="from-orange-500 to-red-500"
            />
            <FeatureCard
              icon={<Zap className="h-6 w-6" />}
              title="Lightning Fast"
              description="Real-time updates, instant search, and blazing-fast performance. Your team will love using it daily."
              color="from-yellow-500 to-orange-500"
            />
            <FeatureCard
              icon={<Rocket className="h-6 w-6" />}
              title="Easy Setup"
              description="Developers love us. Simple integration, clean APIs, and intuitive admin tools. Get up and running in minutes, not weeks."
              color="from-green-500 to-emerald-500"
            />
            <FeatureCard
              icon={<Globe className="h-6 w-6" />}
              title="Multi-Tenant Ready"
              description="Built for SaaS from day one. Each customer gets their own branded portal and admin dashboard."
              color="from-indigo-500 to-purple-500"
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Start with a 14-day free trial. No credit card required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter Plan */}
            <PricingCard
              name="Starter"
              price="69.99"
              description="Perfect for growing teams"
              features={[
                'Up to 100 users',
                'Unlimited feature requests',
                'Branded voting portal',
                'Email notifications',
                'Basic analytics',
                'Email support',
              ]}
              cta="Start Free Trial"
              popular={false}
            />

            {/* Professional Plan */}
            <PricingCard
              name="Professional"
              price="199"
              description="For scaling product teams"
              features={[
                'Up to 500 users',
                'Everything in Starter',
                'Advanced analytics & insights',
                'Custom domain',
                'Team collaboration tools',
                'Priority support',
                'API access',
              ]}
              cta="Start Free Trial"
              popular={true}
            />

            {/* Enterprise Plan */}
            <PricingCard
              name="Enterprise"
              price="Custom"
              description="For large organizations"
              features={[
                'Unlimited users',
                'Everything in Professional',
                'White-label solution',
                'Dedicated account manager',
                'Custom integrations',
                'SLA guarantee',
                'Advanced security & compliance',
              ]}
              cta="Contact Sales"
              popular={false}
            />
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-primary/10 via-purple-500/10 to-pink-500/10 rounded-2xl border border-border p-12">
            <div className="text-center space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                Trusted by product teams everywhere
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Join hundreds of companies using ProductWorkshop to build better products
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8">
                <StatCard number="10K+" label="Feature Requests" />
                <StatCard number="50K+" label="Votes Cast" />
                <StatCard number="500+" label="Happy Customers" />
                <StatCard number="99.9%" label="Uptime" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
            Ready to build better products?
          </h2>
          <p className="text-xl text-muted-foreground">
            Start your 14-day free trial today. No credit card required.
          </p>

          {user ? (
            <Link to="/dashboard">
              <Button size="lg" className="min-w-[200px]">
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button size="lg" className="min-w-[200px]">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-6">
            {/* Brand */}
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-semibold text-foreground">ProductWorkshop</span>
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm">
              <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
            </div>

            {/* Copyright */}
            <p className="text-sm text-muted-foreground">
              © 2025 ProductWorkshop, Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  color
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}) {
  return (
    <div className="group relative bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:border-primary/50">
      <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${color} mb-4`}>
        <div className="text-white">
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
        {title}
      </h3>
      <p className="text-muted-foreground leading-relaxed">
        {description}
      </p>
    </div>
  );
}

function PricingCard({
  name,
  price,
  description,
  features,
  cta,
  popular
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  popular: boolean;
}) {
  return (
    <div className={`relative bg-card border rounded-2xl p-8 ${
      popular ? 'border-primary shadow-xl scale-105' : 'border-border'
    }`}>
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="bg-gradient-to-r from-primary to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
            <Crown className="h-4 w-4" />
            Most Popular
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-foreground mb-2">{name}</h3>
        <p className="text-muted-foreground text-sm mb-4">{description}</p>
        <div className="flex items-baseline justify-center gap-1">
          {price === 'Custom' ? (
            <span className="text-4xl font-bold text-foreground">Custom</span>
          ) : (
            <>
              <span className="text-2xl font-semibold text-muted-foreground">$</span>
              <span className="text-5xl font-bold text-foreground">{price}</span>
              <span className="text-muted-foreground">/month</span>
            </>
          )}
        </div>
      </div>

      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
              popular ? 'text-primary' : 'text-green-500'
            }`} />
            <span className="text-sm text-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      <Link to="/login" className="block">
        <Button
          className={`w-full ${
            popular
              ? 'bg-primary hover:bg-primary/90'
              : 'bg-transparent border border-primary text-primary hover:bg-primary hover:text-white'
          }`}
        >
          {cta}
          {cta !== 'Contact Sales' && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </Link>
    </div>
  );
}

function StatCard({ number, label }: { number: string; label: string }) {
  return (
    <div className="space-y-2">
      <div className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
        {number}
      </div>
      <div className="text-sm text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
